import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, Loader2, X, CheckCircle, AlertCircle, Info, ClipboardCheck } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { primaryText, semiBoldText, baseTextSize, smallTextSize, borderPrimary, secondaryText } from '../../../../styling/stylingHelper';
import { getAccessToken } from '../../../../services/authentication/supabaseService';
import { useMenusStore } from '../../../../stores/appMenusStore';
import { useCostPriceStore } from '../../../../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../../../../stores/appCostPriceCategoriesStore';
import { isPracticeActive } from '../../../onboarding/practice/practiceGuards';

type OverallType = 'all' | 'food' | 'drinks';
type AutomationMode = 'both' | 'categories' | 'costprice';

interface CostPriceAutomationModalProps {
  restaurantId: string;
  overallType: OverallType;
  // When the cost price page menu filter has exactly one real menu selected,
  // we pass that id here so the dropdown defaults to it. We show a small note
  // when this default is in effect so the assignment is not accidental.
  defaultMenuId?: string;
  variant?: 'standard' | 'new_menu';
  onMenuImported?: (menuId: string) => void;
  onClose: () => void;
}

const NO_MENU = '';

const CostPriceAutomationModal: React.FC<CostPriceAutomationModalProps> = ({
  restaurantId,
  overallType,
  defaultMenuId,
  variant = 'standard',
  onMenuImported,
  onClose,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<AutomationMode>('both');
  const isNewMenu = variant === 'new_menu';
  const [selectedMenuId, setSelectedMenuId] = useState<string>(isNewMenu ? NO_MENU : defaultMenuId ?? NO_MENU);
  const [targetMode, setTargetMode] = useState<'create' | 'existing'>('create');
  const [newMenuName, setNewMenuName] = useState('');
  const [refreshFailed, setRefreshFailed] = useState(false);
  // Default VAT from overallType: drinks → 21%, food/all → 9% (most common case). User can override.
  const [vatRate, setVatRate] = useState<'21' | '9'>(overallType === 'drinks' ? '21' : '9');

  const { t } = useTranslation();
  const menus = useMenusStore((s) => s.menus);
  const loadMenuItems = useMenusStore((s) => s.loadMenuItems);
  const loadCostPrices = useCostPriceStore((s) => s.load);
  const loadAutomationRuns = useCostPriceStore((s) => s.loadAutomationRuns);
  const navigate = useNavigate();
  const targetMenus = menus.filter(menu => menu.restaurantId === restaurantId);
  const selectedMenu = targetMenus.find(menu => menu.id === selectedMenuId);
  const trimmedMenuName = newMenuName.trim();
  const duplicateMenuName = targetMenus.some(menu => menu.name.trim().toLowerCase() === trimmedMenuName.toLowerCase());
  const validTarget = targetMode === 'create'
    ? trimmedMenuName.length > 0 && trimmedMenuName.length <= 100 && !duplicateMenuName
    : !!selectedMenu;
  const copy = (key: string, standard: string) => isNewMenu ? t(`costPrice.newMenuAutomation.${key}`) : standard;

  const menuAssignmentDisabled = mode === 'categories';
  const showDefaultedNote = !!defaultMenuId && selectedMenuId === defaultMenuId;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setResult(null);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (isProcessing) return;
    if (isNewMenu && isPracticeActive()) {
      setError(t('costPrice.newMenuAutomation.practiceUnavailable'));
      return;
    }
    if (!selectedFile) {
      setError(copy('selectFile', 'Please select a file'));
      return;
    }
    if (isNewMenu && !validTarget) {
      setError(t('costPrice.newMenuAutomation.menu_required'));
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResult(null);
    setRefreshFailed(false);

    try {
      const formData = new FormData();
      formData.append('restaurantId', restaurantId);
      formData.append('overallType', overallType);
      formData.append('mode', isNewMenu ? 'both' : mode);
      formData.append('vatRate', vatRate);
      if (isNewMenu) {
        formData.append('variant', variant);
        formData.append(targetMode === 'create' ? 'newMenuName' : 'menuId', targetMode === 'create' ? trimmedMenuName : selectedMenuId);
      } else if (selectedMenuId && !menuAssignmentDisabled) {
        formData.append('menuId', selectedMenuId);
      }
      formData.append('file', selectedFile);

      const accessToken = await getAccessToken();

      const response = await fetch('/api/costprice/cost-price-automation', {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        if (isNewMenu && ['menu_required', 'invalid_menu', 'duplicate_menu_name'].includes(data.error)) {
          throw new Error(t(`costPrice.newMenuAutomation.${data.error}`));
        }
        throw new Error(data.details || (isNewMenu ? t('costPrice.newMenuAutomation.processFailed') : data.error || 'Failed to process template file'));
      }

      if (isNewMenu) {
        try {
          await loadCostPrices(restaurantId, { force: true });
          await loadAutomationRuns(restaurantId);
          await useCostPriceStore.getState().loadCustomIngredients(restaurantId);
          await useCostPriceCategoriesStore.getState().load(restaurantId);
          let menuItemsMissing = false;
          if (data.menu?.id) {
            if (data.menu.created) await useMenusStore.getState().reload();
            const menuItems = await loadMenuItems(data.menu.id);
            menuItemsMissing = data.itemsAssignedToMenu > 0 && menuItems.length === 0;
            onMenuImported?.(data.menu.id);
          }
          const costPriceState = useCostPriceStore.getState();
          setRefreshFailed(!!(costPriceState.error || costPriceState.automationRunsError || costPriceState.customIngredientsError || useCostPriceCategoriesStore.getState().error || useMenusStore.getState().error || menuItemsMissing));
        } catch (refreshError) {
          console.error('Failed to refresh new menu after automation:', refreshError);
          setRefreshFailed(true);
        }
        setResult(data);
        return;
      }

      setResult(data);

      try {
        await loadCostPrices(restaurantId, { force: true });
        await loadAutomationRuns(restaurantId);
        await useCostPriceCategoriesStore.getState().load(restaurantId);
        if (data.runId) await useCostPriceStore.getState().loadCustomIngredients(restaurantId);
        const state = useCostPriceStore.getState();
        setRefreshFailed(!!(state.error || state.automationRunsError || state.customIngredientsError || useCostPriceCategoriesStore.getState().error));
      } catch (refreshError) {
        console.error('Failed to refresh automation results:', refreshError);
        setRefreshFailed(true);
      }

      // Refresh the assigned menu's items so the cost price page reflects the change.
      if (selectedMenuId && !menuAssignmentDisabled && data.itemsAssignedToMenu > 0) {
        try {
          await loadMenuItems(selectedMenuId);
        } catch (err) {
          console.error('Failed to refresh menu items after automation:', err);
        }
      }
    } catch (err: any) {
      setError(err.message || copy('processFailed', 'An error occurred while processing the template file'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/60 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-background dark:bg-background-dark rounded-none sm:rounded-lg max-w-2xl w-full h-full sm:h-auto max-h-none sm:max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className={cn(primaryText, semiBoldText, baseTextSize)}>
            {copy('title', 'Cost Price Automation')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <p className={cn(primaryText, smallTextSize, "opacity-70")}>
            {copy('intro', 'Upload your menu template (.docx) to automatically create cost price analyses for your menu items with ingredients.')}
          </p>

          {/* Mode Selector */}
          {!isNewMenu && <div>
            <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
              Mode
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="automation-mode"
                  value="both"
                  checked={mode === 'both'}
                  onChange={() => setMode('both')}
                  disabled={isProcessing}
                  className="mt-1"
                />
                <div>
                  <div className={cn(primaryText, semiBoldText, smallTextSize)}>Both (categories + cost prices)</div>
                  <div className={cn(primaryText, "text-xs opacity-60")}>
                    Create missing categories, create/update items, match revenue items, and match ingredients.
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="automation-mode"
                  value="categories"
                  checked={mode === 'categories'}
                  onChange={() => setMode('categories')}
                  disabled={isProcessing}
                  className="mt-1"
                />
                <div>
                  <div className={cn(primaryText, semiBoldText, smallTextSize)}>Categories only</div>
                  <div className={cn(primaryText, "text-xs opacity-60")}>
                    Create missing categories, create stub items for missing ones, and move existing items to the template's category. No price or ingredient matching.
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="automation-mode"
                  value="costprice"
                  checked={mode === 'costprice'}
                  onChange={() => setMode('costprice')}
                  disabled={isProcessing}
                  className="mt-1"
                />
                <div>
                  <div className={cn(primaryText, semiBoldText, smallTextSize)}>Cost prices only</div>
                  <div className={cn(primaryText, "text-xs opacity-60")}>
                    Create/update items with prices, match revenue items, and match ingredients. Does not create new categories — missing ones fall back to default.
                  </div>
                </div>
              </label>
            </div>
          </div>}

          {/* Menu Assignment */}
          {!isNewMenu && !menuAssignmentDisabled && (
            <div>
              <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
                Assign to menu (optional)
              </label>
              <p className={cn(primaryText, "text-xs opacity-60 mb-2")}>
                Every cost price item from the template (newly created and existing ones matched by name) will be added to this menu. Leave as "— None —" to skip menu assignment.
              </p>
              <select
                value={selectedMenuId}
                onChange={(e) => setSelectedMenuId(e.target.value)}
                disabled={isProcessing}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background dark:bg-background-dark text-primaryText dark:text-primaryText-dark"
              >
                <option value={NO_MENU}>— None —</option>
                {menus.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}{m.isActive ? '' : ' (inactive)'}
                  </option>
                ))}
              </select>
              {showDefaultedNote && (
                <div className="mt-2 flex items-start gap-2 text-xs text-blue-700 dark:text-blue-300">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Defaulted to the menu currently selected in your cost price page filter. Change to "— None —" if you don't want to assign.
                  </span>
                </div>
              )}
            </div>
          )}

          {isNewMenu && (
            <fieldset className="space-y-3 min-w-0" disabled={isProcessing}>
              <legend className={cn(primaryText, semiBoldText, smallTextSize, 'mb-2')}>
                {t('costPrice.newMenuAutomation.targetMenu')}
              </legend>
              <label className={cn(primaryText, smallTextSize, 'flex items-center gap-2')}>
                <input type="radio" name="target-menu-mode" checked={targetMode === 'create'} onChange={() => setTargetMode('create')} />
                {t('costPrice.newMenuAutomation.createMenu')}
              </label>
              {targetMode === 'create' && (
                <div>
                  <label htmlFor="new-menu-name" className={cn(primaryText, smallTextSize, 'block mb-1')}>{t('costPrice.newMenuAutomation.menuName')}</label>
                  <input id="new-menu-name" value={newMenuName} onChange={event => setNewMenuName(event.target.value)} maxLength={100}
                    className="w-full min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background dark:bg-background-dark text-primaryText dark:text-primaryText-dark" />
                  {!result && duplicateMenuName && trimmedMenuName && <p role="alert" className="mt-1 text-sm text-red-700 dark:text-red-400">{t('costPrice.newMenuAutomation.duplicateNameHint')}</p>}
                </div>
              )}
              <label className={cn(primaryText, smallTextSize, 'flex items-center gap-2')}>
                <input type="radio" name="target-menu-mode" checked={targetMode === 'existing'} onChange={() => {
                  setTargetMode('existing');
                  if (!selectedMenu) setSelectedMenuId(targetMenus[0]?.id ?? NO_MENU);
                }} />
                {t('costPrice.newMenuAutomation.existingMenu')}
              </label>
              {targetMode === 'existing' && (
                <div>
                  <select aria-label={t('costPrice.newMenuAutomation.targetMenu')} value={selectedMenuId} onChange={event => setSelectedMenuId(event.target.value)}
                    className="w-full min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background dark:bg-background-dark text-primaryText dark:text-primaryText-dark">
                    {targetMenus.map(menu => <option key={menu.id} value={menu.id}>{menu.name}{menu.isActive ? '' : t('costPrice.newMenuAutomation.inactiveSuffix')}</option>)}
                  </select>
                  {targetMenus.length === 0 && <p className={cn(secondaryText, smallTextSize, 'mt-1')}>{t('costPrice.newMenuAutomation.noMenus')}</p>}
                </div>
              )}
              <p className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 text-sm text-blue-800 dark:text-blue-200">{t('costPrice.newMenuAutomation.info')}</p>
              {targetMode === 'existing' && selectedMenu && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-sm text-amber-800 dark:text-amber-200">
                  <p>{t('costPrice.newMenuAutomation.existingWarning')}</p>
                  {selectedMenu.isActive && <p className="mt-2">{t('costPrice.newMenuAutomation.activeWarning')}</p>}
                </div>
              )}
            </fieldset>
          )}

          {/* VAT Rate Selector */}
          <div>
            <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
              {copy('vatRate', 'VAT Rate')}
            </label>
            <p className={cn(primaryText, "text-xs opacity-60 mb-2")}>
              {copy('vatDescription', 'Applied to every item from this upload to compute the ex-VAT price. Default is 9% for food, 21% for drinks — change if your menu uses a different rate.')}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setVatRate('21')}
                disabled={isProcessing}
                className={cn(
                  "flex-1 px-4 py-3 rounded-md border transition-colors",
                  vatRate === '21'
                    ? "border-darkGreenColor dark:border-darkGreenColor-dark bg-[#F1FAF7] dark:bg-cardLightGreen-dark text-darkGreenColor dark:text-darkGreenColor-dark font-semibold"
                    : cn(borderPrimary, "bg-white hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark", secondaryText),
                  "disabled:opacity-50"
                )}
              >
                21%
              </button>
              <button
                type="button"
                onClick={() => setVatRate('9')}
                disabled={isProcessing}
                className={cn(
                  "flex-1 px-4 py-3 rounded-md border transition-colors",
                  vatRate === '9'
                    ? "border-darkGreenColor dark:border-darkGreenColor-dark bg-[#F1FAF7] dark:bg-cardLightGreen-dark text-darkGreenColor dark:text-darkGreenColor-dark font-semibold"
                    : cn(borderPrimary, "bg-white hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark", secondaryText),
                  "disabled:opacity-50"
                )}
              >
                9%
              </button>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
              {copy('uploadTitle', 'Upload Template File')}
            </label>
            <div className="flex items-center gap-4">
              <label className={cn("flex-1 flex items-center justify-center px-4 py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:border-accent dark:hover:border-accent-dark transition-colors", isNewMenu && 'min-w-0')}>
                <div className={cn('text-center', isNewMenu && 'min-w-0 break-words')}>
                  <Upload className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-2" />
                  <span className={cn(primaryText, smallTextSize)}>
                    {selectedFile ? selectedFile.name : copy('chooseFile', 'Click to upload or drag and drop')}
                  </span>
                  <p className={cn(primaryText, "text-xs opacity-60 mt-1")}>
                    {copy('fileType', 'Word Document (.docx)')}
                  </p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileChange}
                  disabled={isNewMenu && isProcessing}
                />
              </label>
            </div>
            {selectedFile && (
              <div className="mt-2 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <FileText className="h-4 w-4" />
                <span className={isNewMenu ? 'min-w-0 break-words' : undefined}>{selectedFile.name}</span>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h3 className={cn(primaryText, semiBoldText, smallTextSize, "text-red-800 dark:text-red-300 mb-1")}>
                  {copy('errorTitle', 'Error')}
                </h3>
                {/* Validation failures are a bullet list of lines to fix — keep the line breaks */}
                <p className={cn(primaryText, "text-sm text-red-700 dark:text-red-400 whitespace-pre-line break-words max-h-60 overflow-y-auto")}>
                  {error}
                </p>
              </div>
            </div>
          )}

          {/* Result Display */}
          {result && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className={cn(primaryText, semiBoldText, smallTextSize, "text-green-800 dark:text-green-300 mb-1")}>
                    {copy('successTitle', 'Processing Successful')}
                  </h3>
                  <p className={cn(primaryText, "text-sm text-green-700 dark:text-green-400")}>
                    {isNewMenu
                      ? t(!result.menu && !result.runId ? 'costPrice.newMenuAutomation.noDishes' : 'costPrice.newMenuAutomation.successBody')
                      : result.message || 'Template file processed successfully'}
                  </p>
                  {result.runId && (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate(`/costprice/automation-review/${result.runId}`);
                      }}
                      className="mt-3 w-full px-4 py-3 bg-accent dark:bg-accent-dark text-white dark:text-white rounded-md font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    >
                      <ClipboardCheck className="h-5 w-5" />
                      {t('costPrice.automationRun.reviewResults')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {isNewMenu && result?.menuAssignmentFailed && (
            <p role="alert" className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-800 dark:text-red-300">
              {t('costPrice.newMenuAutomation.assignmentFailed')}
            </p>
          )}
          {result && refreshFailed && (
            <p role="alert" className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-sm text-amber-800 dark:text-amber-200">
              {t('costPrice.automationRun.refreshFailed')}
            </p>
          )}

          {/* Submit Button */}
          <div className={isNewMenu ? 'flex flex-col sm:flex-row gap-3 pt-4' : 'flex gap-3 pt-4'}>
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-primaryText dark:text-primaryText-dark rounded-md font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              {copy('cancel', 'Cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedFile || isProcessing || (isNewMenu && !validTarget)}
              className="flex-1 px-6 py-3 bg-accent dark:bg-accent-dark text-white dark:text-white rounded-md font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {copy('processing', 'Processing...')}
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5" />
                  {copy('submit', 'Process Template')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostPriceAutomationModal;
